import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  Stack,
  TextField,
  Typography,
  Tooltip,
  ImageListItemBar,
} from "@mui/material";
import { useTheme } from "@emotion/react";

export default function SmsTemplateSelection({
  open,
  setSelected,
  setClosed,
  templates,
  refreshTemplates,
}) {
  const COLOR_HEADER = "#1E1E1E";
  const COLOR_ACCENT = "#0DA3E2";
  const theme = useTheme();
  const primaryColor = theme.palette.text.primary;

  // Track the selected template for propagation
  const [temp, setTemp] = useState(null);

  // One global search query instead of three separate filters
  const [query, setQuery] = useState("");

  // Visible items in the grid
  const [filteredItems, setFilteredItems] = useState(templates);

  /* ------------------------------------------------------
   * Effects
   * ----------------------------------------------------*/

  // Bubble selection up when the user chooses a template
  useEffect(() => {
    if (temp) setSelected(temp);
  }, [temp, setSelected]);

  // Apply global search filtering whenever the query or templates change
  useEffect(() => {
    const tokens = query.toLowerCase().trim().split(/\s+/).filter(Boolean);

    if (!tokens.length) {
      setFilteredItems(templates);
      return;
    }

    setFilteredItems(
      templates.filter((item) => {
        const haystacks = [
          String(item.Id ?? "").toLowerCase(),
          (item.TemplateName ?? "").toLowerCase(),
          (item.Content ?? "").toLowerCase(),
          (item.Description ?? "").toLowerCase(),
        ];
        return tokens.every((tok) => haystacks.some((h) => h.includes(tok)));
      })
    );
  }, [query, templates]);

  /* ------------------------------------------------------
   * Render
   * ----------------------------------------------------*/

  return (
    <Dialog
      open={open}
      maxWidth="lg"
      sx={{
        "& .MuiDialog-paper": {
          height: "90vh",
          width: 800,
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Sticky Search Bar */}
      <Box
        sx={{
          backgroundColor: "background.default",
          position: "sticky",
          top: 0,
          zIndex: 10,
          borderBottom: "1px solid #ccc",
          p: 2,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          sx={{ width: "100%" }}
        >
          <TextField
            label="Search"
            variant="outlined"
            size="small"
            fullWidth
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </Stack>
      </Box>

      {/* Scrollable Content Grid */}
      <Box
        sx={{
          flexGrow: 1,
          backgroundColor: "background.paper",
          overflowY: "auto",
          p: 2,
          display: "grid",
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          gap: 3,
        }}
      >
        {filteredItems.length ? (
          filteredItems.map((item) => (
            <Box
              key={item.Id}
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                alignItems: "center",
                border: 1,
                borderRadius: 2,
                boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
                minHeight: 200,
                maxHeight: 200,
                backgroundColor: "background.paper",
                overflow: "hidden",
                width: "100%",
                position: "relative",
                transition: "box-shadow 0.3s ease-in-out",
                "&:hover": {
                  boxShadow: "0px 4px 15px rgba(0, 0, 0, 0.3)",
                },
              }}
            >
              {/* Title Bar */}
              <Tooltip placement="top" title={item.TemplateName ?? ""}>
                <ImageListItemBar
                  sx={{
                    backgroundColor: "background.default",
                    width: "100%",
                    textOverflow: "ellipsis",
                    textAlign: "center",
                    position: "static",
                    fontWeight: 500,
                    "& .MuiImageListItemBar-title": {
                      color: primaryColor,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    },
                  }}
                  title={item.TemplateName ?? ""}
                  onClick={() => setTemp(item)}
                />
              </Tooltip>

              {/* Text preview */}
              <Box
                sx={{
                  flexGrow: 1,
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 1,
                }}
              >
                <Typography variant="body2" sx={{ p: 1 }}>
                  {(item.Content ?? "").slice(0, 120)}
                </Typography>
              </Box>

              {/* Select Button Bar */}
              <ImageListItemBar
                sx={{
                  backgroundColor: "#0da2e1",
                  height: "20%",
                  width: "100%",
                  position: "static",
                  borderBottomLeftRadius: 8,
                  borderBottomRightRadius: 8,
                  cursor: "pointer",
                  textAlign: "center",
                }}
                title="Select"
                onClick={() => setTemp(item)}
              />
            </Box>
          ))
        ) : (
          <Typography>No Templates Found</Typography>
        )}
      </Box>

      {/* Footer Buttons */}
      <Box
        sx={{
          p: 2,
          borderTop: 1,
          display: "flex",
          justifyContent: "flex-end",
          gap: 1,
          backgroundColor: "background.default",
        }}
      >
        <Button
          variant="outlined"
          sx={{
            width: "187px",
            height: "50px",
            minWidth: "187px",
            borderRadius: "7px",
            textTransform: "none",
            borderColor: COLOR_ACCENT,
            color: "#FFFFFF",
            bgcolor: COLOR_HEADER,
            "&:hover": {
              borderColor: COLOR_ACCENT,
              bgcolor: "rgba(13, 163, 226, 0.08)",
            },
          }}
          onClick={refreshTemplates}
        >
          Refresh
        </Button>
        <Button
          variant="outlined"
          sx={{
            width: "187px",
            height: "50px",
            minWidth: "187px",
            borderRadius: "7px",
            textTransform: "none",
            borderColor: COLOR_ACCENT,
            color: "#FFFFFF",
            "&:hover": {
              borderColor: COLOR_ACCENT,
              bgcolor: "rgba(13, 163, 226, 0.08)",
            },
          }}
          onClick={setClosed}
        >
          Cancel
        </Button>
      </Box>
    </Dialog>
  );
}
